import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useEffect, useState} from 'react';

import {TraversalDirection} from '../../generated/graphql';
import {TruncatedText} from '../TruncatedText';
import {InstrumentedLoader as Loader} from '../utility/InstrumentedLoader';
import {DAG_REQUEST_LIMIT} from './ArtifactDag';
import {runNodeInputType} from './InfoDrawerRuns';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {extractRunNodeInputType, nodeType} from './LayoutDagUtils';
import {useRunArtifactDagQueryCaller} from './queries/runArtifactDag.query';
import {Cluster, Dag, Run, TOWARD_ARTIFACT} from './utils';

type ClusterRunNodeProps = {
  runNode: Run;
  updateDag: (
    newDag: Dag,
    expandedArtifact: string,
    expandedRun: string
  ) => void;
  setIsDrawerOpen: (val: boolean) => void;
  setDrawerType: (val: nodeType) => void;
  setRunNodeInput: (val: runNodeInputType) => void;
  filterGeneratedArtifacts: boolean;
  cluster: Cluster;
  clusterSize?: number;
  directLineage: boolean;
};

export const ClusterRunNode = ({
  runNode,
  updateDag,
  setIsDrawerOpen,
  setDrawerType,
  setRunNodeInput,
  filterGeneratedArtifacts,
  clusterSize,
  directLineage,
  cluster,
}: ClusterRunNodeProps) => {
  const [dagQueryInput, setDagQueryInput] = useState({
    entityName: '',
    projectName: '',
    runName: '',
    limit: DAG_REQUEST_LIMIT,
    filterGeneratedArtifacts,
    clusterSize,
    directLineage,
  });

  const clusterEdge =
    cluster.clusterEdges.length > 0 ? cluster.clusterEdges[0] : null;
  const clusterEdgeDir =
    clusterEdge != null
      ? clusterEdge.direction.toString() === TOWARD_ARTIFACT
        ? TraversalDirection.Upstream
        : TraversalDirection.Downstream
      : undefined;

  // Get node ids of artifacts this cluster is currently attached to so we attach the new node to these artifacts
  const baseGraphArtifactNodes = cluster.clusterEdges.map(
    n => n.artifactNodeID
  );

  const newDag = useRunArtifactDagQueryCaller(
    dagQueryInput.entityName,
    dagQueryInput.projectName,
    dagQueryInput.runName,
    dagQueryInput.limit,
    dagQueryInput.filterGeneratedArtifacts,
    dagQueryInput.clusterSize,
    dagQueryInput.directLineage,
    clusterEdgeDir,
    baseGraphArtifactNodes
  );
  useEffect(() => {
    if (dagQueryInput.entityName === '') {
      return;
    }
    if (!newDag.loading && newDag.dag != null) {
      updateDag(newDag.dag, '', runNode.runNodeID);
    }
  }, [
    dagQueryInput.entityName,
    newDag.loading,
    newDag.dag,
    runNode.runNodeID,
    updateDag,
  ]);

  if (newDag.loading) {
    return <Loader name="lineage-dag-cluster-run-node" inline size="tiny" />;
  }

  return (
    <>
      <Tailwind style={{width: '100%'}}>
        <div className="group flex w-full items-center justify-between px-[10px] py-[6px]">
          <div
            className="w-full"
            onClick={() => {
              setIsDrawerOpen(true);
              setDrawerType(nodeType.RUN);
              setRunNodeInput(extractRunNodeInputType(runNode));
            }}>
            <TruncatedText>{runNode?.displayName}</TruncatedText>
            <div className="text-[13px] font-normal text-moon-650">
              <TruncatedText>{runNode?.jobType}</TruncatedText>
            </div>
          </div>

          <Tooltip
            content={'Break out'}
            position="bottom center"
            trigger={
              <Icon
                name="log-out"
                onClick={() => {
                  setDagQueryInput({
                    ...dagQueryInput,
                    ...runNode,
                    runName: runNode.runName,
                  });
                }}
                className="invisible h-[16px] w-[16px] group-hover:visible"
              />
            }></Tooltip>
        </div>
      </Tailwind>
    </>
  );
};
